import queryString from 'query-string';
import { isString } from 'lodash';
import { PayerError, ServerResponseJsonSyntaxError, ApiResponseError, HttpUnauthorizedError } from './errors';
import { WrappedResponse } from './WrappedResponse';

const ISE_MESSAGE = 'If the problem persists, please contact Payer.';

const stringifyUrl = (url: string, query: Query = {}) => {
  return queryString.stringifyUrl(
    { url: url, query: query },
    {
      // note that undefined values are always filtered out
      arrayFormat: 'comma',
      skipEmptyString: true,
      skipNull: true,
      sort: false,
    },
  );
};

export type Methods = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'HEAD' | 'OPTIONS' | 'TRACE' | 'CONNECT';
export type Headers = Record<string, string | number | boolean>;
export type QueryValue = string | number | boolean | undefined | null;
export type Query = Record<string, QueryValue | QueryValue[]>;

export type ApiFetchBaseOptions = {
  query?: Query;
  method?: Methods;
  headers?: Headers;
  body?: any;
  bodyIsJson?: boolean;
  responseType?: 'json' | 'text' | 'blob' | 'empty';
};

const apiFetchBase = async <T>(
  url: string,
  { query = {}, method, headers = {}, body, bodyIsJson = true, responseType }: ApiFetchBaseOptions = {},
  showOnlyHandledErrorDetails?: boolean,
): Promise<T | string | Blob | null> => {
  const baseHeaders = bodyIsJson
    ? {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      }
    : undefined;
  const response = await fetch(stringifyUrl(url, query), {
    method: method ?? 'GET',
    headers:
      baseHeaders || headers
        ? {
            ...(baseHeaders || {}),
            ...(headers || {}),
          }
        : undefined,
    body: isString(body) ? body : bodyIsJson ? JSON.stringify(body) : body,
    credentials: 'include',
  });

  const contentType = response.headers.get('Content-Type');

  if (response.ok && responseType === 'blob') {
    if (response.status === 204) {
      return null;
    }
    return await response.blob();
  }

  let responseText: string | null = await response.text();
  let responseJson: T | null = null;
  let responseError: { code?: string; detail?: string; message?: string } | null = null;

  if (contentType && contentType.includes('application/json')) {
    try {
      if (response.ok) {
        responseJson = JSON.parse(responseText);
      } else {
        responseError = JSON.parse(responseText);
      }
    } catch (error) {
      if (response.status === 204) {
        return null;
      }
      throw new ServerResponseJsonSyntaxError({ responseText, cause: error });
    }
  } else if (responseText === '' && contentType === null) {
    responseText = null;
  }

  if (response.ok) {
    return responseJson || responseText;
  } else if (response.status === 401) {
    throw new HttpUnauthorizedError();
  } else if (responseError?.code || responseError?.message || responseError?.detail) {
    if (showOnlyHandledErrorDetails && response.status !== 400 && response.status !== 404 && !responseError?.code) {
      throw new ApiResponseError({
        message: ISE_MESSAGE,
      });
    } else {
      throw new ApiResponseError({
        code: responseError?.code,
        message: responseError?.message,
        detail: responseError?.detail,
      });
    }
  } else if (response.status === 504) {
    throw new PayerError({ code: 'serverUnavailable' });
  } else {
    if (showOnlyHandledErrorDetails) {
      throw new PayerError({
        code: 'serverResponseNotJson',
        message: ISE_MESSAGE,
      });
    } else {
      throw new PayerError({
        code: 'serverResponseNotJson',
        detail: `status: ${response.statusText} response: '${responseText}'`,
      });
    }
  }
};

// TODO when no api calls use apiFetchBase, refactor it so that it has the same function as this method and remove it
const wrappedApiFetchBase = <T>(
  url: string,
  options?: ApiFetchBaseOptions,
  showOnlyHandledErrorDetails?: boolean,
): WrappedResponse<T> => {
  return new WrappedResponse(apiFetchBase<T>(url, options, showOnlyHandledErrorDetails));
};

export { stringifyUrl, apiFetchBase, wrappedApiFetchBase };
